import { z } from 'zod';
import { jobNameSchema } from '../jobs.js';
import { cursorPageSchema, cursorQuerySchema } from './pagination.js';
/**
 * Контракт «мёртвого письма» — списка задач, исчерпавших попытки.
 *
 * Почему сбой дублируется в нашу таблицу, хотя BullMQ и сам хранит упавшие
 * задачи в Redis. Три причины, и все из опыта эксплуатации.
 *
 * Первая: Redis — кэш по своей природе. Его чистят, перезапускают без
 * сохранения, переносят; терять историю сбоев вместе с ним нельзя.
 * Вторая: у нас многопользовательская система, и оператор должен видеть сбои
 * своего воркспейса и только их. В Redis задачи лежат без разделения прав.
 * Третья: сбой нужно связать с сущностью — диалогом, заказом, конверсией —
 * и присоединить к нему запросом в базе. Через Redis это невозможно.
 *
 * Поэтому Redis остаётся транспортом, а операторский список сбоев живёт
 * в PostgreSQL рядом с данными, к которым он относится.
 */
export const jobFailureStatusSchema = z.enum(['open', 'acknowledged', 'resolved']);
export const jobFailureSchema = z.object({
    id: z.uuid(),
    queueName: z.string(),
    jobName: z.string(),
    /** Идентификатор задачи в BullMQ — по нему её можно найти в Redis, пока она там. */
    jobId: z.string(),
    attemptsMade: z.number().int().nonnegative(),
    attemptsAllowed: z.number().int().positive(),
    failedReason: z.string(),
    /** Первые строки стека. Целиком не храним: смысла мало, места занимает много. */
    stackExcerpt: z.string().nullable(),
    /** Полезная нагрузка задачи, уже без секретов. */
    payload: z.unknown().nullable(),
    requestId: z.string().nullable(),
    status: jobFailureStatusSchema,
    firstFailedAt: z.iso.datetime(),
    lastFailedAt: z.iso.datetime(),
    /** Сколько раз задача с таким же ключом падала насовсем. */
    occurrences: z.number().int().positive(),
});
export const jobFailureQuerySchema = cursorQuerySchema.extend({
    status: jobFailureStatusSchema.optional(),
    jobName: z.string().max(100).optional(),
});
export const jobFailurePageSchema = cursorPageSchema(jobFailureSchema);
export const updateJobFailureInputSchema = z.object({
    status: jobFailureStatusSchema,
});
/** Постановка диагностической задачи из интерфейса. */
export const enqueueDiagnosticsInputSchema = z.object({
    behaviour: z.enum(['ok', 'fail']).default('fail'),
    delayMs: z.number().int().min(0).max(30_000).default(0),
    note: z.string().max(200).default('проверка очереди'),
});
export const enqueuedJobSchema = z.object({
    jobId: z.string(),
    jobName: jobNameSchema,
    queueName: z.string(),
    attemptsAllowed: z.number().int().positive(),
});
/** Сводка состояния очереди для экрана диагностики. */
export const queueStatsSchema = z.object({
    queueName: z.string(),
    waiting: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    delayed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    paused: z.boolean(),
});
//# sourceMappingURL=jobs.js.map